/* eslint-disable no-console */
import { parseRawSession } from '@portal/appLayer/auth/utils';
import { SSFlagValue } from '@portal/state/context/StatsigContext/StatsigFlags';
import { COOKIE_STORE_KEY } from '@portal/state/cookies/agStore';
import { StatsigClient } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { ALLOWED_AXON_EVENTS } from './axon-events';
import { ALLOWED_BLOTOUT_EVENTS } from './blotout-events';
import { GAEventName } from './ga-event-name.enum';
import { RUDDERSTACK_ALLOWED_EVENTS } from './rudderstack-events';
import { ALLOWED_STATSIG_EVENTS } from './statsig-events';
import { DataLayerEvent } from './types';

const ANALYTICS_DEBUG_COOKIE = 'analytics_debug';

const checkEventDestinations = (eventName: string) => {
  return {
    GTM: true, // GTM receives all events
    Blotout: ALLOWED_BLOTOUT_EVENTS.has(eventName as GAEventName),
    Axon: ALLOWED_AXON_EVENTS.has(eventName as GAEventName),
    Rudderstack: RUDDERSTACK_ALLOWED_EVENTS.has(eventName as GAEventName),
    Statsig: ALLOWED_STATSIG_EVENTS.has(eventName as GAEventName)
  };
};

const shouldLogAnalytics = () => {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  return Cookies.get(ANALYTICS_DEBUG_COOKIE) === 'true';
};

export const consoleLogAnalyticsEvent = (eventName: string, dataLayerEvent: DataLayerEvent) => {
  if (!shouldLogAnalytics()) {
    return;
  }

  const destinations = checkEventDestinations(eventName);
  const enabledServices = Object.entries(destinations)
    .filter(([, willReceive]) => willReceive)
    .map(([service]) => service);
  const disabledServices = Object.entries(destinations)
    .filter(([, willReceive]) => !willReceive)
    .map(([service]) => service);

  console.groupCollapsed(`[Analytics] ${eventName}`);

  console.info('%cTimestamp:', 'font-weight: bold;', new Date().toISOString());

  console.info('%cEvent:', 'font-weight: bold;', eventName);

  if (dataLayerEvent.event_type) {
    console.info('%cEvent Type:', 'font-weight: bold;', dataLayerEvent.event_type);
  }

  console.info('%cPayload:', 'font-weight: bold;', dataLayerEvent);

  console.info('%cDestinations:', 'font-weight: bold;');
  console.info('    %c✅ %s', 'color: #059669;', enabledServices.join(', '));
  if (disabledServices.length > 0) {
    console.info('    %c❌ %s', 'color: #dc2626;', disabledServices.join(', '));
  }

  console.groupEnd();
};

// Helper function to enhance dataLayer event with additional information
export const enhanceDataLayerEvent = (dataLayerEvent: DataLayerEvent) => {
  const store = Cookies.get(COOKIE_STORE_KEY);
  const { sendExperimentAssignments = [], ...restOfDataLayerEvent } = dataLayerEvent;
  const modifiedDataLayerEvent = {
    ...restOfDataLayerEvent
  };

  try {
    const sessionKey = `ag:${store}:session`;
    const rawSession = localStorage.getItem(sessionKey);
    const sessionData = parseRawSession(rawSession ?? '');

    if (sessionData) {
      const { customer } = sessionData;
      const { tags } = customer;

      // Add tags to DL event if available
      if (tags?.length) modifiedDataLayerEvent.customerTags = tags;
    }
  } catch (err) {
    console.error('Error parsing session data:', err);
  }

  try {
    const experimentAssignments = sendExperimentAssignments.reduce(
      (acc, flag) => {
        const { groupName } = StatsigClient.instance().getExperiment(flag, {
          disableExposureLog: true // Don't relog exposure when sending assignment data to DL
        });
        if (groupName) acc[flag] = groupName;
        return acc;
      },
      {} as Record<SSFlagValue, string>
    );

    if (Object.keys(experimentAssignments).length > 0) {
      modifiedDataLayerEvent.experimentAssignments = experimentAssignments;
    }
  } catch (err) {
    console.error('Error processing experiment assignments:', err);
  }

  return modifiedDataLayerEvent;
};
